import axios from 'axios';
import { 
  AppointmentDetailsResponse, 
  FeedbackSubmitDTO, 
  ReviewStep 
} from '../types';
import { API_ENDPOINTS } from '../config/apiConfig';

class ReviewService {
  private appointmentDetails?: AppointmentDetailsResponse;
  private appointmentId?: string;
  private companyName?: string;
  private reviewStep: ReviewStep = 'add';
  private tipAmount?: number;
  private isOldReview = false;
  private isOldV2Version = false;
  private fedId?: number; // Selected feedback ID for negative reviews
  private activeRequests: Map<string, Promise<any>> = new Map(); // Cache for ongoing requests

  async getAppointmentDetails(id: string, version = 'v1'): Promise<AppointmentDetailsResponse> {
    const cacheKey = `appointment-${id}-${version}`;
    
    // Return cached data if available
    if (this.appointmentDetails && this.appointmentId === id) {
      return this.appointmentDetails;
    }
    
    // Return existing request if in progress
    if (this.activeRequests.has(cacheKey)) {
      return this.activeRequests.get(cacheKey);
    }
    
    try {
      const requestPromise = axios.get<AppointmentDetailsResponse>(
        API_ENDPOINTS.REVIEW_APP.APPOINTMENT_DETAILS(version, id)
      ).then(response => {
        this.appointmentDetails = response.data;
        this.appointmentId = id;
        this.activeRequests.delete(cacheKey);
        return response.data;
      });
      
      // Cache the ongoing request
      this.activeRequests.set(cacheKey, requestPromise);
      
      return await requestPromise;
    } catch (error) {
      this.activeRequests.delete(cacheKey);
      console.error('Error fetching appointment details:', error);
      throw error;
    }
  }

  async submitReview(feedback: FeedbackSubmitDTO): Promise<any> {
    try {
      // Get headers with company-id and location-id if appointment details are available
      const headers = this.getHeaders();
      const response = await axios.post(API_ENDPOINTS.REVIEW_APP.FEEDBACK_SUBMIT(), feedback, { headers });
      return response.data;
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  async clickReview(appointmentId: number): Promise<AppointmentDetailsResponse> {
    try {
      // Get headers with company-id and location-id if appointment details are available
      const headers = this.getHeaders();
      const response = await axios.post<AppointmentDetailsResponse>(
        API_ENDPOINTS.REVIEW_APP.REVIEW_CLICK(), 
        { appointment_id: appointmentId },
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Error clicking review:', error);
      throw error;
    }
  }

  async trackLinkOpen(appointmentId: number): Promise<any> {
    try {
      const headers = this.getHeaders();
      const response = await axios.post(
        API_ENDPOINTS.REVIEW_APP.TRACK_LINK_OPEN(),
        { appointment_id: appointmentId },
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Error tracking link open:', error);
      throw error;
    }
  }

  // Helper method to get headers with company-id and location-id
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add company-id and location-id headers if appointment details are available
    if (this.appointmentDetails?.appointment_details) {
      const { CompanyID, LocationID } = this.appointmentDetails.appointment_details;
      if (CompanyID) {
        headers['company-id'] = CompanyID.toString();
      }
      if (LocationID) {
        headers['location-id'] = LocationID.toString();
      }
    }

    return headers;
  }

  // Getters and Setters
  getAppointmentDetailsData(): AppointmentDetailsResponse | undefined {
    return this.appointmentDetails;
  }

  setAppointmentDetailsData(details: AppointmentDetailsResponse): void {
    this.appointmentDetails = details;
  }

  getAppointmentId(): string | undefined {
    return this.appointmentId;
  }

  setAppointmentId(id: string): void {
    this.appointmentId = id;
  }

  getCompanyName(): string | undefined {
    return this.companyName;
  }

  setCompanyName(name: string): void {
    this.companyName = name;
  }

  getReviewStep(): ReviewStep {
    return this.reviewStep;
  }

  setReviewStep(step: ReviewStep): void {
    this.reviewStep = step;
  }

  getTipAmount(): number | undefined {
    return this.tipAmount;
  }

  setTipAmount(amount: number): void {
    this.tipAmount = amount;
  }

  getOldReview(): boolean {
    return this.isOldReview;
  }

  setOldReview(isOld: boolean): void {
    this.isOldReview = isOld;
  }

  getOldV2Version(): boolean {
    return this.isOldV2Version;
  }

  setOldV2Version(isV2: boolean): void {
    this.isOldV2Version = isV2;
  }

  getFedId(): number | undefined {
    return this.fedId;
  }

  setFedId(id: number): void {
    this.fedId = id;
  }
}

export const reviewService = new ReviewService();
export default reviewService;
